import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/_module/components/ui/card';
import { Button } from '@/app/_module/components/ui/button';
import { Input } from '@/app/_module/components/ui/input';
import { Badge } from '@/app/_module/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/_module/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/app/_module/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/app/_module/components/ui/tooltip';
import { Dialog, ResponsiveDialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/app/_module/components/ui/dialog';
import { Phone, MapPin, Search, ShoppingCart, Check, User, MessageSquare, AlertTriangle, Sparkles, TrendingUp, Target, CheckCircle, Info, Flame, Zap, Star, Crosshair, Circle, ArrowRight, DollarSign } from 'lucide-react';
import { usePhoneNumberPricing, usePhoneNumberManager } from '@/app/_module/hooks/usePhoneNumberPricing';
import { useToast } from '@/app/_module/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import api from '@/app/_module/services/api';
import { getApiUrl } from '@/app/_module/services/config';
import { endpoint } from '@/app/_module/services/endpoints';
import { handleApiCall } from '@/app/_module/utils/apiResponseHandler';
import { useUserContext } from '@/app/_module/store/contexts/userContext';

// Plans that pay $2/mo per number; the rest pay $4. Same list as the backend's
// paysTwoDollarNumbers(), which picks the Stripe price: this grid left out super_agent
// and showed $4 for a $2 plan.
const TWO_DOLLAR_NUMBER_PLANS = ['full_fledged', 'enterprise', 'super_agent'];
const paysTwoDollarNumbers = (accountType?: string | null) =>
  TWO_DOLLAR_NUMBER_PLANS.includes(accountType?.toLowerCase() || '');

interface AreaCode {
  code: number;
  state: string;
  cost: number;
  // Recommendation fields (populated from analytics)
  priority?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  reason?: string;
  leadCount?: number;
  historicalCallCount?: number;
  isOwned?: boolean;
  // Availability fields (from available numbers check)
  stockLevel?: 'high' | 'low' | 'none' | null;
  overlays?: number[];
  metro?: string;
}

interface AreaCodeAvailabilityData {
  areaCode: number;
  available: boolean;
  availableCount: number;
  checkedAt: string;
  overlays: number[];
  metro: string;
  stockLevel: 'high' | 'low' | 'none';
}

interface AreaCodeRecommendation {
  areaCode: string;
  state: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  estimatedCost: number;
  reason: string;
  leadCount: number;
  historicalCallCount: number;
  isAvailable: boolean;
}

interface Agent {
  id: string;
  name: string;
  email: string;
  a2pEnabled: boolean;
  a2pStatus: 'verified' | 'pending' | 'rejected' | 'not_submitted';
  agencyId?: string;
  agencyName?: string;
  isSelf?: boolean;
  accountType?: string; // basic, full_fledged, enterprise
}

interface AreaCodePurchaseGridProps {
  onAreaCodeSelect: (areaCode: number, state: string, agentId?: string) => void;
  selectedAreaCodes: Set<string>;
  /** Max numbers per single purchase (Stripe checkout caps at 20 line items). */
  maxSelectable?: number;
  isPurchasing: boolean;
  onPurchase: (areaCode: number, state: string, agentId?: string) => Promise<void>;
  onBulkCheckout?: (interval: 'month' | 'year', agentId?: string) => Promise<void>;
  isInvitedAgent?: boolean;
  agencyName?: string;
  isAgencyOwner?: boolean;
}

export function AreaCodePurchaseGrid({
  onAreaCodeSelect,
  selectedAreaCodes,
  maxSelectable = 20,
  isPurchasing,
  onPurchase,
  onBulkCheckout,
  isInvitedAgent = false,
  agencyName,
  isAgencyOwner = false
}: AreaCodePurchaseGridProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCodes, setFilteredCodes] = useState<AreaCode[]>([]);
  const [areaCodes, setAreaCodes] = useState<AreaCode[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>("myself");
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [recommendations, setRecommendations] = useState<Map<string, AreaCodeRecommendation>>(new Map());
  const [showRecommendationDialog, setShowRecommendationDialog] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<AreaCodeRecommendation | null>(null);
  const [showOnlyRecommended, setShowOnlyRecommended] = useState(false);
  const { toast} = useToast();

  // Get user context for immediate access to accountType (no async fetch needed)
  const { user } = useUserContext();

  // Determine user's price based on accountType - this is available immediately from context
  const userAccountType = user?.user?.accountType?.toLowerCase() || '';
  const isUserFullFledged = paysTwoDollarNumbers(userAccountType);
  const userBasePrice = isUserFullFledged ? 2 : 4;

  // Get user's owned phone numbers to mark area codes as owned
  const { phoneNumbers } = usePhoneNumberManager();

  // Fetch area codes from the database
  const { data: areaCodesData, isLoading, error } = useQuery({
    queryKey: ['area-codes'],
    queryFn: async () => {
      const result = await handleApiCall<any>(async () => {
        const response = await api.get<{ statusCode: number; data: { areaCodes: any[]; total: number } }>({
          url: getApiUrl(endpoint.communication.getCommunication),
        });
        return response;
      });
      return result;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: pricingData } = usePhoneNumberPricing();

  // Fetch agency agents for agency owners (including sub-agencies hierarchy)
  const { data: agentsData } = useQuery({
    queryKey: ['agency-agents-hierarchy'],
    queryFn: async () => {
      if (!isAgencyOwner) return [];
      const result = await handleApiCall<Agent[]>(async () => {
        const response = await api.get<{ data: Agent[] }>({
          url: getApiUrl(endpoint.agencies.agents) + '?includeHierarchy=true',
        });
        return response;
      });
      return result || [];
    },
    enabled: isAgencyOwner,
  });

  const agents: Agent[] = (() => {
    const raw = agentsData || [];
    const seen = new Set<string>();
    return raw.filter((agent) => {
      if (seen.has(agent.id)) return false;
      seen.add(agent.id);
      return true;
    });
  })();

  // Find the current user (isSelf) in agents list
  const currentUser = agents.find(agent => agent.isSelf);

  // Fetch area code recommendations based on call history and leads
  // Backend returns: { data: { recommendations: [...flat array...], highPriority, mediumPriority, lowPriority } }
  const { data: recommendationsData, isLoading: isLoadingRecommendations } = useQuery({
    queryKey: ['area-code-recommendations'],
    queryFn: async () => {
      const result = await handleApiCall<{
        recommendations: AreaCodeRecommendation[];
        totalRecommendations: number;
        highPriority: number;
        mediumPriority: number;
        lowPriority: number;
      }>(async () => {
        const response = await api.get<{ statusCode: number; data: any }>({
          url: getApiUrl(endpoint.communication.areaCodePurchaseRecommendations),
          auth: true,
        });
        return response;
      });
      return result;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
  });

  // Fetch area code availability from available numbers cache
  const { data: availabilityData } = useQuery({
    queryKey: ['area-code-availability'],
    queryFn: async () => {
      const result = await handleApiCall<{
        areaCodes: AreaCodeAvailabilityData[];
        summary: { total: number; available: number; lowStock: number; unavailable: number };
      }>(async () => {
        const response = await api.get<{ statusCode: number; data: any }>({
          url: getApiUrl(endpoint.communication.getAreaCodeAvailability),
          auth: true,
        });
        return response;
      });
      return result;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (backend caches for 1 hour)
    retry: 1,
  });

  // Live re-checks for codes the user has actually engaged with. The cached grid
  // is a hint; the moment someone selects a code we ask the provider directly,
  // because that is the reading their money depends on. Layered OVER the cached
  // map so it wins wherever it exists.
  const [liveOverrides, setLiveOverrides] = useState<
    Map<number, { available: boolean; stockLevel: 'high' | 'low' | 'none'; willSubstitute?: boolean; overlays?: number[] }>
  >(new Map());

  const liveCheck = useCallback(async (codes: number[]) => {
    const wanted = codes.filter(Boolean).slice(0, 25);
    if (wanted.length === 0) return;
    try {
      const res: any = await api.post({
        url: getApiUrl(endpoint.areaCodes.availabilityLive),
        body: { areaCodes: wanted },
        auth: true,
      });
      const rows = res?.data?.data?.results ?? res?.data?.results ?? [];
      if (!Array.isArray(rows) || rows.length === 0) return;
      setLiveOverrides((prev) => {
        const next = new Map(prev);
        for (const r of rows) {
          // `unknown` means the check failed — never downgrade a code on that basis.
          if (r?.unknown) continue;
          next.set(Number(r.areaCode), {
            available: !!r.inStock || !!r.willSubstitute,
            stockLevel: r.inStock ? 'high' : r.willSubstitute ? 'low' : 'none',
            willSubstitute: !!r.willSubstitute,
            overlays: r.overlays || [],
          });
        }
        return next;
      });
    } catch {
      // A failed live check must never make the grid worse than the cached view.
    }
  }, []);

  // Build availability map
  const availabilityMap = useMemo(() => {
    const map = new Map<number, AreaCodeAvailabilityData>();
    if (availabilityData?.areaCodes && Array.isArray(availabilityData.areaCodes)) {
      availabilityData.areaCodes.forEach((ac: AreaCodeAvailabilityData) => {
        map.set(ac.areaCode, ac);
      });
    }
    return map;
  }, [availabilityData]);

  // Build recommendations map when data changes
  // Backend returns a flat array of recommendations, already sorted by priority
  useEffect(() => {
    if (recommendationsData?.recommendations && Array.isArray(recommendationsData.recommendations)) {
      const recMap = new Map<string, AreaCodeRecommendation>();

      recommendationsData.recommendations.forEach((rec: AreaCodeRecommendation) => {
        recMap.set(rec.areaCode, rec);
      });

      setRecommendations(recMap);
    }
  }, [recommendationsData]);

  // Get owned area codes from user's phone numbers
  const ownedAreaCodes = useMemo(() => {
    const owned = new Set<string>();
    if (phoneNumbers && Array.isArray(phoneNumbers)) {
      phoneNumbers.forEach((phone: any) => {
        const areaCode = phone.areaCode?.toString() ||
          (phone.number || phone.phoneNumber || '')
            .replace(/\D/g, '')
            .substring(0, 3);
        if (areaCode && areaCode.length === 3) {
          owned.add(areaCode);
        }
      });
    }
    return owned;
  }, [phoneNumbers]);

  // Transform data when areaCodesData changes
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load area codes. Please try again.",
        variant: "destructive",
      });
    } else if (areaCodesData?.items && Array.isArray(areaCodesData.items)) {
      // Standard format: data.items[]
      const codes: AreaCode[] = areaCodesData.items.map((item: any) => ({
        code: parseInt(item.code),
        state: item.stateName || item.stateCode || item.state,
        cost: userBasePrice,
      }));

      setAreaCodes(codes);
      setFilteredCodes(codes);
    } else if (areaCodesData && areaCodesData.areaCodes && Array.isArray(areaCodesData.areaCodes)) {
      // Legacy format: data.areaCodes[]
      const codes: AreaCode[] = areaCodesData.areaCodes.map((item: any) => ({
        code: parseInt(item.code),
        state: item.stateName || item.stateCode || item.state,
        cost: userBasePrice,
      }));

      setAreaCodes(codes);
      setFilteredCodes(codes);
    } else if (areaCodesData && Array.isArray(areaCodesData)) {
      // Direct array response
      const codes: AreaCode[] = areaCodesData.map((item: any) => ({
        code: parseInt(item.code),
        state: item.stateName || item.stateCode || item.state,
        cost: userBasePrice,
      }));

      setAreaCodes(codes);
      setFilteredCodes(codes);
    }
  }, [areaCodesData, userBasePrice, error, toast]);

  // Filter and sort area codes - recommended first, then by priority
  useEffect(() => {
    let filtered = areaCodes;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = areaCodes.filter(areaCode =>
        areaCode.code.toString().includes(searchTerm) ||
        areaCode.state.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply "show only recommended" filter
    if (showOnlyRecommended) {
      filtered = filtered.filter(areaCode => recommendations.has(areaCode.code.toString()));
    }

    // Enrich with recommendation data, owned status, and availability
    const enrichedCodes = filtered.map(areaCode => {
      const codeStr = areaCode.code.toString();
      const rec = recommendations.get(codeStr);
      const isOwned = ownedAreaCodes.has(codeStr);
      const live = liveOverrides.get(areaCode.code);
      const cachedAvailability = availabilityMap.get(areaCode.code);
      const availability = live
        ? { ...cachedAvailability, ...live }
        : cachedAvailability;

      return {
        ...areaCode,
        priority: rec?.priority || null,
        reason: rec?.reason,
        leadCount: rec?.leadCount,
        historicalCallCount: rec?.historicalCallCount,
        isOwned,
        stockLevel: availability?.stockLevel || null,
        overlays: availability?.overlays || [],
        metro: availability?.metro || '',
      };
    });

    // Sort: HIGH priority first, then MEDIUM, then LOW, then owned, then rest
    const priorityOrder: Record<string, number> = { 'HIGH': 0, 'MEDIUM': 1, 'LOW': 2 };
    enrichedCodes.sort((a, b) => {
      // Owned codes go after recommendations but before non-recommended
      const aOrder = a.priority ? priorityOrder[a.priority] : (a.isOwned ? 3 : 4);
      const bOrder = b.priority ? priorityOrder[b.priority] : (b.isOwned ? 3 : 4);

      if (aOrder !== bOrder) return aOrder - bOrder;

      // Secondary sort by lead count (higher first)
      if ((a.leadCount || 0) !== (b.leadCount || 0)) {
        return (b.leadCount || 0) - (a.leadCount || 0);
      }

      // Tertiary sort by area code
      return a.code - b.code;
    });

    setFilteredCodes(enrichedCodes);
  }, [searchTerm, areaCodes, recommendations, ownedAreaCodes, showOnlyRecommended, availabilityMap]);

  // Show recommendation details in a dialog
  const handleShowRecommendation = (areaCode: AreaCode) => {
    const rec = recommendations.get(areaCode.code.toString());
    if (rec) {
      setSelectedRecommendation(rec);
      setShowRecommendationDialog(true);
    }
  };

  // Get styles for area code card based on priority/ownership/availability
  const getCardStyles = (areaCode: AreaCode) => {
    const key = `${areaCode.code}-${areaCode.state}`;
    const isSelected = selectedAreaCodes.has(key);

    if (isSelected) {
      return 'ring-2 ring-primary bg-primary/5';
    }

    if (areaCode.isOwned) {
      return 'bg-green-50 border-green-300 border-2';
    }

    // Unavailable area codes with no overlays get dimmed
    if (areaCode.stockLevel === 'none' && (!areaCode.overlays || areaCode.overlays.length === 0)) {
      return 'opacity-50 bg-gray-100 border-gray-300 border cursor-not-allowed';
    }

    // Unavailable but has overlays - show subtle warning
    if (areaCode.stockLevel === 'none') {
      return 'bg-red-50/50 border-red-200 border hover:bg-red-50';
    }

    if (areaCode.priority === 'HIGH') {
      return 'bg-red-50 border-red-400 border-2 animate-pulse-subtle';
    }

    if (areaCode.priority === 'MEDIUM') {
      return 'bg-yellow-50 border-yellow-400 border-2';
    }

    if (areaCode.priority === 'LOW') {
      return 'bg-orange-50 border-orange-300 border-2';
    }

    return 'hover:bg-gray-50';
  };

  // Get icon color based on priority
  const getIconBgColor = (areaCode: AreaCode) => {
    if (areaCode.isOwned) return 'bg-green-100';
    if (areaCode.priority === 'HIGH') return 'bg-red-100';
    if (areaCode.priority === 'MEDIUM') return 'bg-yellow-100';
    if (areaCode.priority === 'LOW') return 'bg-orange-100';
    return 'bg-primary/10';
  };

  const getIconColor = (areaCode: AreaCode) => {
    if (areaCode.isOwned) return 'text-green-600';
    if (areaCode.priority === 'HIGH') return 'text-red-600';
    if (areaCode.priority === 'MEDIUM') return 'text-yellow-600';
    if (areaCode.priority === 'LOW') return 'text-orange-600';
    return 'text-primary';
  };

  const handleAreaCodeClick = async (areaCode: number, state: string) => {
    const key = `${areaCode}-${state}`;
    const availability = availabilityMap.get(areaCode);

    // Warn user if area code is unavailable but allow selection (backend has overlay fallback)
    if (availability?.stockLevel === 'none' && !selectedAreaCodes.has(key)) {
      const overlayList = availability.overlays || [];
      if (overlayList.length > 0) {
        toast({
          title: `Limited availability for (${areaCode})`,
          description: `This area code may not have available numbers. The system will automatically try overlay codes (${overlayList.join(', ')}) in the same metro area during provisioning.`,
        });
      } else {
        toast({
          title: `Unavailable: (${areaCode})`,
          description: 'This area code currently has no available numbers and no overlay alternatives. Purchase may fail.',
          variant: 'destructive',
        });
        return; // Block selection for truly unavailable codes with no overlays
      }
    }

    // No A2P check needed for purchasing - A2P is only required to USE the number for SMS
    // Agents can buy area codes without A2P verification

    // If onBulkCheckout is provided, just toggle selection (don't purchase immediately)
    if (onBulkCheckout) {
      // Selecting is the moment the reading starts to matter — verify it live.
      // Fire-and-forget: the grid stays responsive and updates when it lands.
      if (!selectedAreaCodes.has(key)) void liveCheck([parseInt(String(areaCode), 10)]);
      onAreaCodeSelect(areaCode, state, selectedAgent === "myself" ? undefined : selectedAgent);
    } else {
      // Legacy behavior: immediate purchase
      if (selectedAreaCodes.has(key)) {
        // Already selected, deselect
        onAreaCodeSelect(areaCode, state, selectedAgent === "myself" ? undefined : selectedAgent);
      } else {
        // Not selected, select and purchase
        onAreaCodeSelect(areaCode, state, selectedAgent === "myself" ? undefined : selectedAgent);
        try {
          await onPurchase(areaCode, state, selectedAgent === "myself" ? undefined : selectedAgent);
        } catch (error) {

          toast({
            title: "Purchase Failed",
            description: "Failed to purchase area code. Please try again.",
            variant: "destructive",
          });
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Loading Area Codes...</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-20 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-semibold text-red-600">Failed to Load Area Codes</h3>
        <p className="text-gray-600 mt-2">Please try refreshing the page.</p>
      </div>
    );
  }

  // Calculate pricing based on selected agent's account type
  const getSelectedAgentPrice = () => {
    if (selectedAgent === "myself") {
      // Primary: Use userBasePrice derived from userContext (available immediately, no async)
      // This ensures correct pricing on first render
      return userBasePrice;
    } else {
      // Use selected agent's account type
      const selectedAgentData = agents.find(a => a.id === selectedAgent);
      const isFullFledged = selectedAgentData && paysTwoDollarNumbers(selectedAgentData.accountType);
      return isFullFledged ? 2 : 4;
    }
  };

  const basePrice = getSelectedAgentPrice();
  const pricePerCode = billingInterval === 'year' ? basePrice * 12 * 0.9 : basePrice;
  const totalSelected = selectedAreaCodes.size;
  const totalPrice = totalSelected * pricePerCode;

  // Smart price formatting - shows whole dollars without decimals
  const formatPrice = (price: number) => {
    return price % 1 === 0 ? price.toFixed(0) : price.toFixed(2);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold">Select Area Codes to Purchase</h3>
        <p className="text-gray-600 mt-1">
          Choose area codes for targeted lead calling. Pricing based on your subscription plan.
        </p>
      </div>

      {/* Pricing Badge */}
      <div className="flex items-center justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-50 border border-gray-200">
          <DollarSign className="h-4 w-4 text-green-600" />
          <span className="text-sm text-gray-600">Per area code:</span>
          <span className="text-sm font-bold text-gray-900">${formatPrice(basePrice)}/mo</span>
        </div>
      </div>

      {/* Availability Summary Banner */}
      {availabilityData?.summary && availabilityData.summary.unavailable > 0 && (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 text-sm font-medium">Area Code Availability</AlertTitle>
          <AlertDescription className="text-amber-700 text-xs">
            {availabilityData.summary.available} available, {availabilityData.summary.lowStock} low stock, {availabilityData.summary.unavailable} unavailable out of {availabilityData.summary.total} total area codes.
            Unavailable codes with overlay alternatives will automatically fall back to a nearby area code during purchase.
          </AlertDescription>
        </Alert>
      )}

      {/* AI Recommendations Summary */}
      {recommendations.size > 0 && (
        <Card className="overflow-hidden border border-primary/20 shadow-md">
          {/* Dark tactical header */}
          <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/20 flex-shrink-0">
                  <Crosshair className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-white font-bold text-base tracking-tight">AI Area Code Recommendations</h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-white/15 text-white border border-white/20">
                      {recommendations.size} suggested
                    </span>
                  </div>
                  <p className="text-black text-xs mt-0.5">
                    Based on your call history and leads — recommended to improve local presence and answer rates.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOnlyRecommended(!showOnlyRecommended)}
                className={`flex-shrink-0 h-8 text-xs border transition-colors ${
                  showOnlyRecommended
                    ? 'bg-white text-primary border-white hover:bg-white/90'
                    : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                }`}
              >
                <Target className="h-3.5 w-3.5 mr-1.5" />
                {showOnlyRecommended ? 'Show All' : 'Show Recommended Only'}
              </Button>
            </div>
          </div>

          {/* Legend row */}
          <div className="px-6 py-3 bg-primary/5 border-b border-primary/10 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm bg-red-400 border border-red-500" />
              <span className="text-red-700 font-medium">High Priority</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm bg-yellow-400 border border-yellow-500" />
              <span className="text-yellow-700 font-medium">Medium Priority</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm bg-orange-300 border border-orange-400" />
              <span className="text-orange-700 font-medium">Low Priority</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500" />
              <span className="text-green-700 font-medium">Available</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Circle className="h-2.5 w-2.5 fill-yellow-500 text-yellow-500" />
              <span className="text-yellow-700 font-medium">Low Stock</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <Circle className="h-2.5 w-2.5 fill-red-500 text-red-500" />
              <span className="text-red-700 font-medium">Unavailable</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-sm bg-green-400 border border-green-500" />
              <span className="text-green-700 font-medium">Already Owned</span>
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="Search by area code or state..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Agent Selection for Agency Owners */}
      {isAgencyOwner && (
        <div className="space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <User className="h-4 w-4" />
            Assign to Agent (Optional)
          </label>
          <Select value={selectedAgent} onValueChange={setSelectedAgent}>
            <SelectTrigger>
              <SelectValue placeholder="Select agent to assign phone number" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {/* Current User (Myself) Option - Always first */}
              <SelectItem value="myself">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-primary" />
                  <span className="font-medium">{currentUser?.name || user?.user?.name || 'Myself'}</span>
                  <Badge variant="secondary" className="text-xs">You</Badge>
                  <Badge variant="outline" className={`text-xs ${isUserFullFledged ? 'text-purple-600 border-purple-300' : 'text-gray-600 border-gray-300'}`}>
                    ${userBasePrice}/mo
                  </Badge>
                </div>
              </SelectItem>

              {/* All other agents - flat list sorted alphabetically */}
              {agents
                .filter(agent => !agent.isSelf)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((agent) => {
                  const isA2PReady = agent.a2pEnabled && agent.a2pStatus === 'verified';
                  const isFullFledged = paysTwoDollarNumbers(agent.accountType);
                  const agentPrice = isFullFledged ? 2 : 4;
                  return (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <span>{agent.name}</span>
                        <span className="text-xs text-gray-400">({agent.agencyName})</span>
                        <Badge variant="outline" className={`text-xs ${isFullFledged ? 'text-purple-600 border-purple-300' : 'text-gray-600 border-gray-300'}`}>
                          ${agentPrice}/mo
                        </Badge>
                        {isA2PReady ? (
                          <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                            <MessageSquare className="h-3 w-3 mr-1" />
                            SMS
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            Calls Only
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
            </SelectContent>
          </Select>

          {/* Show warning when non-A2P agent is selected */}
          {selectedAgent && selectedAgent !== "myself" && (() => {
            const selectedAgentData = agents.find(a => a.id === selectedAgent);
            const isA2PReady = selectedAgentData?.a2pEnabled && selectedAgentData?.a2pStatus === 'verified';
            if (!isA2PReady && selectedAgentData) {
              return (
                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-700">
                    <p className="font-medium">{selectedAgentData.name} can only make calls</p>
                    <p className="text-amber-600 mt-0.5">
                      Please alert this agent to complete A2P verification if they need to send SMS messages.
                    </p>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {selectedAgent && (
            <p className="text-xs text-gray-500">
              {selectedAgent === "myself"
                ? `Phone number will be assigned to you`
                : `Phone number will be assigned to ${agents.find(a => a.id === selectedAgent)?.name} (${agents.find(a => a.id === selectedAgent)?.agencyName})`
              }
            </p>
          )}
        </div>
      )}

      {/* Area Codes Grid */}
      <TooltipProvider>
        <div className="max-h-[32rem] overflow-y-auto px-4 pt-4 pb-4 border-2 border-primary/30 rounded-lg bg-primary/5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
            {filteredCodes.map((areaCode) => {
              const key = `${areaCode.code}-${areaCode.state}`;
              const isSelected = selectedAreaCodes.has(key);
              const hasRecommendation = areaCode.priority !== null && areaCode.priority !== undefined;

              return (
                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <Card
                      className={`cursor-pointer transition-all hover:shadow-lg relative ${getCardStyles(areaCode)}`}
                      onClick={() => {
                        handleAreaCodeClick(areaCode.code, areaCode.state);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-center flex-col space-y-2">
                          {/* Priority Badge - Top Left */}
                          {areaCode.priority && (
                            <div className="absolute top-2 left-2">
                              {areaCode.priority === 'HIGH' && (
                                <Badge variant="destructive" className="text-xs flex items-center gap-1">
                                  <Flame className="h-3 w-3" />
                                  HIGH
                                </Badge>
                              )}
                              {areaCode.priority === 'MEDIUM' && (
                                <Badge className="text-xs bg-yellow-500 hover:bg-yellow-600 flex items-center gap-1">
                                  <Zap className="h-3 w-3" />
                                  MEDIUM
                                </Badge>
                              )}
                              {areaCode.priority === 'LOW' && (
                                <Badge variant="outline" className="text-xs border-orange-400 text-orange-600">
                                  LOW
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Owned Badge - Top Left (if owned) */}
                          {areaCode.isOwned && (
                            <div className="absolute top-2 left-2">
                              <Badge className="text-xs bg-green-600 hover:bg-green-700 flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                OWNED
                              </Badge>
                            </div>
                          )}

                          {/* Info button for recommendations */}
                          {hasRecommendation && !areaCode.isOwned && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="absolute top-2 right-2 h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowRecommendation(areaCode);
                              }}
                            >
                              <Info className="h-4 w-4 text-primary" />
                            </Button>
                          )}

                          {/* Selected check mark */}
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <Check className="h-5 w-5 text-primary" />
                            </div>
                          )}

                          <div className={`${getIconBgColor(areaCode)} p-3 rounded-lg`}>
                            {areaCode.isOwned ? (
                              <CheckCircle className={`h-5 w-5 ${getIconColor(areaCode)}`} />
                            ) : areaCode.priority === 'HIGH' ? (
                              <Flame className={`h-5 w-5 ${getIconColor(areaCode)}`} />
                            ) : areaCode.priority === 'MEDIUM' ? (
                              <Zap className={`h-5 w-5 ${getIconColor(areaCode)}`} />
                            ) : (
                              <Phone className={`h-5 w-5 ${getIconColor(areaCode)}`} />
                            )}
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-lg">
                              ({areaCode.code})
                            </div>
                            <div className="text-sm text-gray-600 flex items-center justify-center gap-1">
                              <MapPin className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{areaCode.state}</span>
                            </div>

                            {/* Lead count for recommendations */}
                            {areaCode.leadCount && areaCode.leadCount > 0 && (
                              <div className="text-xs text-primary mt-1 flex items-center justify-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                {areaCode.leadCount} lead{areaCode.leadCount > 1 ? 's' : ''} need this
                              </div>
                            )}

                            {areaCode.isOwned ? (
                              <div className="text-sm font-medium text-green-600 mt-1">
                                ✓ Already owned
                              </div>
                            ) : (
                              <div className="text-sm font-medium text-green-600 mt-1">
                                ${formatPrice(pricePerCode)}/{billingInterval === 'year' ? 'yr' : 'mo'}
                              </div>
                            )}

                            {/* Availability indicator */}
                            {!areaCode.isOwned && areaCode.stockLevel && (
                              <div className="mt-1.5">
                                {areaCode.stockLevel === 'high' && (
                                  <div className="flex items-center justify-center gap-1 text-xs text-green-600">
                                    <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                                    Available
                                  </div>
                                )}
                                {areaCode.stockLevel === 'low' && (
                                  <div className="flex items-center justify-center gap-1 text-xs text-yellow-600">
                                    <Circle className="h-2 w-2 fill-yellow-500 text-yellow-500" />
                                    Low stock
                                  </div>
                                )}
                                {areaCode.stockLevel === 'none' && (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center justify-center gap-1 text-xs text-red-600">
                                      <Circle className="h-2 w-2 fill-red-500 text-red-500" />
                                      Unavailable
                                    </div>
                                    {areaCode.overlays && areaCode.overlays.length > 0 && (
                                      <div className="flex items-center gap-1 text-[10px] text-blue-600">
                                        <ArrowRight className="h-2.5 w-2.5" />
                                        Try {areaCode.overlays.slice(0, 2).map(c => `(${c})`).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {areaCode.isOwned ? (
                      <p>You own this area code. Click to purchase another for a team member - matching area codes increase pickup rates.</p>
                    ) : areaCode.stockLevel === 'none' ? (
                      <div>
                        <p className="font-medium text-red-600">No available numbers</p>
                        {areaCode.overlays && areaCode.overlays.length > 0 ? (
                          <p className="text-sm">
                            Try overlay area codes ({areaCode.overlays.slice(0, 3).join(', ')}) which cover the same {areaCode.metro || 'metro area'}.
                            The system will attempt these automatically at checkout.
                          </p>
                        ) : (
                          <p className="text-sm">No overlay alternatives available for this area code.</p>
                        )}
                      </div>
                    ) : areaCode.reason ? (
                      <div>
                        <p className="font-medium">{areaCode.priority} Priority</p>
                        <p className="text-sm">{areaCode.reason}</p>
                        {areaCode.leadCount && (
                          <p className="text-xs mt-1 text-primary/70">
                            {areaCode.leadCount} leads in this area code
                          </p>
                        )}
                        {areaCode.stockLevel === 'low' && (
                          <p className="text-xs mt-1 text-yellow-600">Low stock - purchase soon</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p>Click to select this area code for purchase</p>
                        {areaCode.stockLevel === 'low' && (
                          <p className="text-xs mt-1 text-yellow-600">Low stock - limited numbers remaining</p>
                        )}
                      </div>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </TooltipProvider>

      {filteredCodes.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No area codes found matching your search.</p>
        </div>
      )}

      {/* Checkout Summary (only show if onBulkCheckout is provided and area codes are selected) */}
      {onBulkCheckout && totalSelected > 0 && (
        <Card className="sticky bottom-0 border-t-4 border-primary">
          <CardContent className="p-4">
            <div className="space-y-3">
              {/* Selected area codes display */}
              <div className="flex flex-wrap gap-2">
                {Array.from(selectedAreaCodes).map((key) => {
                  const [code, state] = key.split('-');
                  return (
                    <Badge
                      key={key}
                      variant="secondary"
                      className="bg-primary/10 text-primary text-sm px-3 py-1 cursor-pointer hover:bg-red-100 hover:text-red-700 transition-colors"
                      onClick={() => onAreaCodeSelect(parseInt(code), state, selectedAgent === "myself" ? undefined : selectedAgent)}
                    >
                      ({code}) {state}
                    </Badge>
                  );
                })}
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {totalSelected} of {maxSelectable} selected
                    {totalSelected >= maxSelectable && (
                      <span className="ml-1.5 text-xs font-normal text-amber-600">· max per purchase reached</span>
                    )}
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    ${formatPrice(totalPrice)} / {billingInterval === 'year' ? 'year' : 'month'}
                  </p>
                  {billingInterval === 'year' && (
                    <p className="text-xs text-green-600">
                      You're saving ${formatPrice(totalSelected * basePrice * 12 * 0.1)} with yearly billing!
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => onBulkCheckout(billingInterval, selectedAgent === "myself" ? undefined : selectedAgent)}
                  disabled={isPurchasing}
                  size="lg"
                  className="gap-2"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {isPurchasing ? 'Processing...' : 'Proceed to Checkout'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendation Details Dialog */}
      <Dialog open={showRecommendationDialog} onOpenChange={setShowRecommendationDialog}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRecommendation?.priority === 'HIGH' && (
                <Flame className="h-5 w-5 text-red-600" />
              )}
              {selectedRecommendation?.priority === 'MEDIUM' && (
                <Zap className="h-5 w-5 text-yellow-600" />
              )}
              {selectedRecommendation?.priority === 'LOW' && (
                <Target className="h-5 w-5 text-orange-600" />
              )}
              Why Buy Area Code {selectedRecommendation?.areaCode}?
            </DialogTitle>
            <DialogDescription>
              {selectedRecommendation?.state}
            </DialogDescription>
          </DialogHeader>

          {selectedRecommendation && (
            <div className="space-y-4 py-4">
              {/* Priority Badge */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Priority:</span>
                {selectedRecommendation.priority === 'HIGH' && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <Flame className="h-3 w-3" />
                    HIGH PRIORITY
                  </Badge>
                )}
                {selectedRecommendation.priority === 'MEDIUM' && (
                  <Badge className="bg-yellow-500 hover:bg-yellow-600 flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    MEDIUM PRIORITY
                  </Badge>
                )}
                {selectedRecommendation.priority === 'LOW' && (
                  <Badge variant="outline" className="border-orange-400 text-orange-600">
                    LOW PRIORITY
                  </Badge>
                )}
              </div>

              {/* Reason */}
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/30">
                <p className="text-sm text-gray-900">
                  {selectedRecommendation.reason}
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <p className="text-xs font-medium text-gray-500">Leads Needing This</p>
                    </div>
                    <p className="text-2xl font-bold text-primary">
                      {selectedRecommendation.leadCount || 0}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="h-4 w-4 text-green-600" />
                      <p className="text-xs font-medium text-gray-500">Historical Calls</p>
                    </div>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedRecommendation.historicalCallCount || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Benefits */}
              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-900">Why Local Numbers Matter:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>3x higher answer rate</strong> - Prospects answer local calls more often</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Trust & credibility</strong> - Local presence builds instant rapport</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Avoid spam filters</strong> - 78% fewer spam blocks on local numbers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Better conversion</strong> - Agents close 2.5x more deals with local presence</span>
                  </li>
                </ul>
              </div>

              {/* Price */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                <span className="font-medium text-green-900">Monthly Cost:</span>
                <span className="text-xl font-bold text-green-600">
                  ${formatPrice(selectedRecommendation.estimatedCost || basePrice)}/mo
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRecommendationDialog(false)}
              className="w-full sm:w-auto"
            >
              Maybe Later
            </Button>
            <Button
              onClick={() => {
                if (selectedRecommendation) {
                  const areaCodeNum = parseInt(selectedRecommendation.areaCode);
                  handleAreaCodeClick(areaCodeNum, selectedRecommendation.state);
                  setShowRecommendationDialog(false);
                  toast({
                    title: "Area Code Added",
                    description: `Area code ${selectedRecommendation.areaCode} has been added to your selection.`,
                  });
                }
              }}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              Add to Cart
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}
